import type { GraphQLError } from 'graphql';

/**
 * Errors come in in with type unknown, but from the Sink interface:
 *
 * An error that has occured. Calling this function "closes" the sink.
 * Besides the errors being `Error` and `readonly GraphQLError[]`, it
 * can also be a `CloseEvent`, but to avoid bundling DOM typings because
 * the client can run in Node env too, you should assert the close event
 * type during implementation.
 *
 * error(error: unknown): void;
 */

export type SinkError = Error | readonly GraphQLError[] | CloseEvent | ErrorEvent;

export const getSinkError = (error: SinkError): Error => {
	let errorInfo: Error;
	if (Array.isArray(error)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		[errorInfo] = error as readonly GraphQLError[];
	} else if (error instanceof Error) {
		errorInfo = error;
	} else if (typeof error === 'object' && 'type' in error && error.type === 'close') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		errorInfo = new Error(`CloseEvent: ${(error as CloseEvent).reason}`);
	} else if (typeof error === 'object' && 'type' in error && error.type === 'error') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		errorInfo = new Error(`ErrorEvent: "${(error as ErrorEvent).error}"`);
	} else if (error instanceof Event) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		errorInfo = new Error(`Event: ${(error as Event).type}`);
	} else {
		errorInfo = new Error(`Unknown sink error: ${error}`);
	}

	if (error instanceof Error && error.stack) {
		errorInfo.stack = error.stack;
	}

	return errorInfo;
};
